import { HttpTypes } from "@medusajs/types"
import { clx } from "@medusajs/ui"
import React from "react"

type OptionValueMetaValue = {
  value: string
  label?: string
  description?: string
  swatchImageUrl?: string
}

type OptionValueMeta = {
  name: string
  values: OptionValueMetaValue[]
}

type OptionSelectProps = {
  option: HttpTypes.StoreProductOption
  current: string | undefined
  updateOption: (title: string, value: string) => void
  title: string
  disabled: boolean
  disabledValues?: string[]
  meta?: OptionValueMeta | null
  "data-testid"?: string
}

const normalizeKey = (value: string) => value.trim().toLowerCase()

const OptionSelect: React.FC<OptionSelectProps> = ({
  option,
  current,
  updateOption,
  title,
  "data-testid": _dataTestId,
  disabled,
  disabledValues = [],
  meta,
}) => {
  // Normalize values: support both Medusa-style { value } objects and string[]
  const filteredOptions = (option.values ?? [])
    .map((v: any) => (typeof v === "string" ? v : v?.value))
    .filter(Boolean) as string[]
  // Prefer using human-readable option name; fall back to id/_id
  const optKey = (option as any).name || (option as any).title || (option as any).id || (option as any)._id || title

  const metaValues = Array.isArray(meta?.values) ? meta!.values : []
  const metaByValue = new Map<string, OptionValueMetaValue>()
  metaValues.forEach((value) => {
    if (!value?.value) return
    metaByValue.set(normalizeKey(value.value), value)
  })
  const hasSwatches = metaValues.some(
    (value) => typeof value?.swatchImageUrl === "string" && value.swatchImageUrl.trim().length > 0
  )

  const selectedMeta = current ? metaByValue.get(normalizeKey(current)) : undefined
  const selectedLabel = selectedMeta?.label || current || ""
  const selectedDescription = selectedMeta?.description?.trim()
  const selectedHeading = selectedLabel
    ? selectedDescription
      ? `${selectedLabel} — ${selectedDescription}`
      : selectedLabel
    : ""

  return (
    <div className={hasSwatches ? "flex flex-col gap-2" : "flex items-center gap-3"}>
      <div className={hasSwatches ? "space-y-1" : ""}>
        <span className="text-black text-sm font-lealti">{title}</span>
        {hasSwatches && selectedHeading ? (
          <div className="text-sm font-lealti-light text-black/80">{selectedHeading}</div>
        ) : null}
      </div>
      <div
        role="radiogroup"
        aria-label={String(title)}
        className={hasSwatches ? "flex flex-wrap gap-2" : "flex flex-wrap gap-2"}
      >
        {filteredOptions.map((v) => {
          const isUnavailable = disabledValues.includes(v)
          const isDisabled = disabled || isUnavailable
          const isSelected = current === v
          const metaValue = metaByValue.get(normalizeKey(v))
          const displayLabel = metaValue?.label || v
          const description = metaValue?.description?.trim()
          const descriptionText = description
            ? `${displayLabel} — ${description}`
            : displayLabel
          const titleText = isUnavailable
            ? `${descriptionText} — Sin stock`
            : descriptionText
          if (hasSwatches) {
            return (
              <button
                key={v}
                type="button"
                role="radio"
                aria-checked={isSelected}
                aria-label={titleText}
                title={titleText}
                disabled={isDisabled}
                onClick={() => {
                  if (isDisabled) return
                  updateOption(String(optKey), v)
                }}
                onKeyDown={(e) => {
                  if (isDisabled) return
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault()
                    updateOption(String(optKey), v)
                  }
                }}
                className={clx(
                  "h-10 w-10 overflow-hidden rounded-md border transition-all duration-150",
                  {
                    "border-black ring-2 ring-black": isSelected,
                    "border-white": !isSelected,
                    "opacity-50 cursor-not-allowed line-through": isDisabled,
                  }
                )}
              >
                {metaValue?.swatchImageUrl ? (
                  <img
                    src={metaValue.swatchImageUrl}
                    alt={displayLabel}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <span className="flex h-full w-full items-center justify-center text-[10px] text-black">
                    {displayLabel}
                  </span>
                )}
              </button>
            )
          }

          return (
            <button
              key={v}
              type="button"
              role="radio"
              aria-checked={isSelected}
              aria-label={titleText}
              disabled={isDisabled}
              onClick={() => {
                if (isDisabled) return
                updateOption(String(optKey), v)
              }}
              onKeyDown={(e) => {
                if (isDisabled) return
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault()
                  updateOption(String(optKey), v)
                }
              }}
              className={clx(
                "inline-flex items-center justify-center text-sm px-3 py-2 border transition-colors duration-150 font-lealti-light rounded-3xl",
                {
                  "bg-black text-white border-black": isSelected,
                  "border-white text-black": !isSelected,
                  "opacity-50 cursor-not-allowed line-through": isDisabled,
                }
              )}
              title={titleText}
            >
              {displayLabel}
            </button>
          )
        })}
      </div>
    </div>
  )
}

export default OptionSelect
